import { Anchor, Card, List, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { useQueries } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import {
  IconCalendarUser,
  IconHeartHandshake,
  IconTicket,
  IconUsersGroup,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { Link } from "react-router";
import { z } from "zod";
import { useMyCareRequests } from "../care/care-queries";
import { formatWhen, useUpcomingEvents } from "../events/event-queries";
import { useFindGroups } from "../groups/group-queries";
import { useMySchedule } from "../my-schedule/church-schedule-queries";
import { semanticColor } from "@how/design-system";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const requestsSchema = z.object({
  members: z.array(z.object({ status: z.string(), firstName: z.string(), lastName: z.string() })),
});

/** A leader's groups with someone waiting for an answer, one roster call per led group. */
function useLedRequests(ledGroupIds: readonly string[]) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQueries({
    queries: ledGroupIds.map((groupId) => ({
      queryKey: ["led-roster", tenantId, groupId],
      retry: false,
      queryFn: async () => {
        const { data, error } = await supabase.rpc("group_roster", { p_group_id: groupId });
        if (error) throw error;
        return requestsSchema.parse(data);
      },
    })),
  });
}

/** What is waiting, in the order it matters: answers others need from me first, then my own news. */
export type WaitingKind = "group_requests" | "serving" | "my_request" | "event" | "care";

export interface WaitingItem {
  readonly key: string;
  readonly kind: WaitingKind;
  readonly icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  readonly text: string;
  readonly to: string;
}

/**
 * Things waiting for you (#166, decided at the second groups checkpoint): requests waiting for a leader's answer,
 * serving dates to answer, your own pending group requests, your next event, your open care requests. Every
 * source is a module the church may not have; a refusal there means nothing to show, not an error.
 */
export function useWaitingItems(): { items: WaitingItem[]; loading: boolean } {
  const groups = useFindGroups();
  const events = useUpcomingEvents();
  const schedule = useMySchedule();
  const care = useMyCareRequests();
  const led = (groups.data ?? []).filter((g) => g.led);
  const rosters = useLedRequests(led.map((g) => g.id));

  const items: WaitingItem[] = [];
  led.forEach((g, index) => {
    const waiting = (rosters[index]?.data?.members ?? []).filter((m) => m.status === "requested");
    if (waiting.length > 0)
      items.push({
        key: `led-${g.id}`,
        kind: "group_requests",
        icon: IconUsersGroup,
        text: `${String(waiting.length)} ${waiting.length === 1 ? "person is" : "people are"} waiting to join ${g.name}`,
        to: "/groups",
      });
  });
  const pending = (schedule.data?.assignments ?? []).filter(
    (a) => !a.isCancelled && a.response === "no_reply",
  );
  if (pending.length > 0)
    items.push({
      key: "serving",
      kind: "serving",
      icon: IconCalendarUser,
      text: `${String(pending.length)} serving ${pending.length === 1 ? "date is" : "dates are"} waiting for your answer`,
      to: "/my-schedule",
    });
  const myRequests = (groups.data ?? []).filter((g) => g.mine?.status === "requested");
  for (const g of myRequests)
    items.push({
      key: `mine-${g.id}`,
      kind: "my_request",
      icon: IconUsersGroup,
      text: `Your request to join ${g.name} is with the leaders`,
      to: "/groups",
    });
  const next = (events.data ?? [])
    .filter(
      (o) =>
        !o.isCancelled &&
        o.mine.some((m) => m.status === "registered" || m.status === "waitlisted"),
    )
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
  if (next !== undefined)
    items.push({
      key: "event",
      kind: "event",
      icon: IconTicket,
      text: `Next: ${next.event.title}, ${formatWhen(next.startsAt, next.event.campus?.timeZone)}`,
      to: "/events",
    });
  const open = (care.data?.requests ?? []).filter((r) => r.status !== "closed");
  if (open.length > 0)
    items.push({
      key: "care",
      kind: "care",
      icon: IconHeartHandshake,
      text: `${String(open.length)} ${open.length === 1 ? "request" : "requests"} for prayer or help ${open.length === 1 ? "is" : "are"} with your church`,
      to: "/care",
    });

  const loading = groups.isPending || events.isPending || schedule.isPending || care.isPending;
  return { items, loading };
}

export function WaitingCard() {
  const { items, loading } = useWaitingItems();
  return (
    <Card data-testid="waiting-card">
      <Stack gap="xs">
        <Title order={2} size="h4">
          Waiting for you
        </Title>
        {items.length === 0 ? (
          <Text size="sm" c="dimmed">
            {loading ? "Looking…" : "Nothing waiting. Enjoy your week."}
          </Text>
        ) : (
          <List spacing={6} size="sm" center>
            {items.map((item) => (
              <List.Item
                key={item.key}
                icon={
                  <ThemeIcon
                    size={20}
                    radius="xl"
                    variant="light"
                    color={semanticColor("info")}
                    aria-hidden
                  >
                    <item.icon size={14} />
                  </ThemeIcon>
                }
              >
                <Anchor component={Link} to={item.to} size="sm">
                  {item.text}
                </Anchor>
              </List.Item>
            ))}
          </List>
        )}
      </Stack>
    </Card>
  );
}
